/**
 * HERMES' CONTENT FORGE 🔥
 * 
 * The Divine Workshop where content is forged in celestial fire.
 * Dark theme with molten orange/amber accents, ember particles, heat shimmer effects.
 * 
 * Features:
 * - The Anvil: Live content creation view
 * - The Caduceus Dashboard: Twin metrics (Reach vs Engagement)
 * - Winged Sandals Quick-Deploy: Instant multi-platform distribution
 * - Forge Temperature Gauge: Pipeline heat indicator
 * - Divine Arsenal View: All content as weapons
 * - Olympus Alliance Panel: Athena → Hermes collaboration
 * - The Messenger's Scroll: Content history timeline
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Flame, Zap, Send, Package, TrendingUp, Calendar, 
  FileText, Image, MessageSquare, Mail, Hash, Target,
  Clock, ChevronRight, Play, Pause, Settings, RefreshCw,
  Award, Shield, Sword, ArrowRight, Eye, Download,
  BarChart3, PieChart, Activity, Users, Globe, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ember Particle Effect Component
const EmberParticles = () => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = Array.from({ length: 25 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 5,
        duration: 3 + Math.random() * 4,
        size: 2 + Math.random() * 4,
        opacity: 0.3 + Math.random() * 0.5
      }));
      setParticles(newParticles);
    };
    generateParticles();
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute rounded-full animate-float-up"
          style={{
            left: `${particle.left}%`,
            bottom: '-10px',
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: `radial-gradient(circle, #f97316 0%, #f59e0b 50%, transparent 100%)`,
            opacity: particle.opacity,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
            boxShadow: '0 0 6px #f97316, 0 0 12px #f59e0b'
          }}
        />
      ))}
      <style>{`
        @keyframes float-up {
          0% { transform: translateY(0) scale(1); opacity: 0.6; }
          50% { opacity: 0.8; }
          100% { transform: translateY(-100vh) scale(0.3); opacity: 0; }
        }
        .animate-float-up {
          animation: float-up linear infinite;
        }
      `}</style>
    </div>
  );
};

// Forge Temperature Gauge Component
const ForgeTemperature = ({ temperature = 75 }) => {
  const getTemperatureColor = (temp) => {
    if (temp < 25) return 'from-blue-500 to-cyan-500';
    if (temp < 50) return 'from-yellow-500 to-amber-500';
    if (temp < 75) return 'from-orange-500 to-red-500';
    return 'from-red-500 to-pink-500';
  };

  const getTemperatureLabel = (temp) => {
    if (temp < 25) return { label: 'Cold', emoji: '❄️' };
    if (temp < 50) return { label: 'Warming', emoji: '🌡️' };
    if (temp < 75) return { label: 'Hot', emoji: '🔥' };
    return { label: 'Divine Fire!', emoji: '⚡' };
  };

  const { label, emoji } = getTemperatureLabel(temperature);

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-orange-500/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500 animate-pulse" />
          <span className="text-sm font-medium text-gray-300">Forge Temperature</span>
        </div>
        <span className="text-2xl">{emoji}</span>
      </div>
      <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${getTemperatureColor(temperature)} rounded-full transition-all duration-1000 ease-out`}
          style={{ width: `${temperature}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-pulse" />
        </div>
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-xs text-gray-500">Cold</span>
        <span className="text-sm font-bold text-orange-400">{label}</span>
        <span className="text-xs text-gray-500">Divine</span>
      </div>
    </div>
  );
};

// The Caduceus Dashboard (Twin Metrics)
const CaduceusDashboard = ({ reach = 0, engagement = 0 }) => {
  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-5 border border-orange-500/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
          <span className="text-xl">🐍</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">The Caduceus</h3>
          <p className="text-xs text-gray-400">Twin serpents of performance</p>
        </div>
      </div>
      
      {/* Intertwined metrics visualization */}
      <div className="relative h-32 mb-4">
        <svg viewBox="0 0 200 100" className="w-full h-full">
          {/* Reach serpent (left) */}
          <path
            d="M 20 80 Q 50 20, 100 50 Q 150 80, 180 20"
            stroke="url(#reachGradient)"
            strokeWidth="4"
            fill="none"
            className="animate-pulse"
          />
          {/* Engagement serpent (right) */}
          <path
            d="M 20 20 Q 50 80, 100 50 Q 150 20, 180 80"
            stroke="url(#engagementGradient)"
            strokeWidth="4"
            fill="none"
            className="animate-pulse"
            style={{ animationDelay: '0.5s' }}
          />
          {/* Gradients */}
          <defs>
            <linearGradient id="reachGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
            <linearGradient id="engagementGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          {/* Center staff */}
          <line x1="100" y1="10" x2="100" y2="90" stroke="#d4d4d8" strokeWidth="3" />
          <circle cx="100" cy="10" r="6" fill="#f59e0b" />
        </svg>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center">
          <div className="text-2xl font-black text-orange-400">{reach.toLocaleString()}</div>
          <div className="text-xs text-gray-400 flex items-center justify-center gap-1">
            <Globe className="w-3 h-3" /> Reach
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-black text-cyan-400">{engagement.toLocaleString()}</div>
          <div className="text-xs text-gray-400 flex items-center justify-center gap-1">
            <Users className="w-3 h-3" /> Engagement
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Launch Pad (The Anvil)
const QuickLaunchPad = ({ onLaunch, isForging }) => {
  const contentTypes = [
    { id: 'blog', icon: FileText, label: 'Blog Post', color: 'from-orange-500 to-red-500', credits: 25 },
    { id: 'social', icon: MessageSquare, label: 'Social Pack', color: 'from-amber-500 to-orange-500', credits: 15 },
    { id: 'email', icon: Mail, label: 'Newsletter', color: 'from-yellow-500 to-amber-500', credits: 10 },
    { id: 'full', icon: Package, label: 'Full Package', color: 'from-red-500 to-orange-600', credits: 35 },
  ];

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-5 border border-orange-500/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center animate-pulse">
          <span className="text-xl">⚒️</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">The Anvil</h3>
          <p className="text-xs text-gray-400">Strike to forge content</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {contentTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => onLaunch(type.id)}
            disabled={isForging}
            className={`
              relative overflow-hidden group p-4 rounded-xl border-2 border-orange-500/50
              bg-gradient-to-br ${type.color}
              transition-all duration-300 transform hover:scale-105 hover:border-orange-400 hover:shadow-lg hover:shadow-orange-500/30
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
            `}
          >
            {/* Glow effect on hover */}
            <div className="absolute inset-0 bg-white/10 group-hover:bg-white/20 transition-all duration-300" />
            
            <div className="relative z-10">
              <type.icon className="w-6 h-6 text-black mb-2 group-hover:scale-110 transition-transform" />
              <div className="text-sm font-black text-black">{type.label}</div>
              <div className="text-xs font-bold text-black/70 mt-1">{type.credits} credits</div>
            </div>
            
            {/* Spark effect */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Sparkles className="w-4 h-4 text-black animate-pulse" />
            </div>
          </button>
        ))}
      </div>
      
      {isForging && (
        <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-orange-400">
            <Flame className="w-4 h-4 animate-pulse" />
            <span className="text-sm font-medium">Hermes is forging...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Divine Arsenal View
const DivineArsenal = ({ packages = [] }) => {
  const getWeaponIcon = (type) => {
    switch(type) {
      case 'blog': return { icon: '⚔️', name: 'Sword', desc: 'Blog Post' };
      case 'social': return { icon: '🏹', name: 'Arrows', desc: 'Social Pack' };
      case 'email': return { icon: '🛡️', name: 'Shield', desc: 'Newsletter' };
      default: return { icon: '⚡', name: 'Lightning', desc: 'Full Package' };
    }
  };

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-5 border border-orange-500/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <span className="text-xl">⚔️</span>
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Divine Arsenal</h3>
            <p className="text-xs text-gray-400">Your content weapons</p>
          </div>
        </div>
        <span className="text-sm text-orange-400 font-bold">{packages.length} items</span>
      </div>
      
      {packages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Your arsenal is empty</p>
          <p className="text-xs mt-1">Forge content to fill it</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
          {packages.slice(0, 10).map((pkg, idx) => {
            const weapon = getWeaponIcon(pkg.type);
            return (
              <div 
                key={pkg.id || idx}
                className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-orange-500/30 transition-colors cursor-pointer group"
              >
                <span className="text-2xl">{weapon.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{pkg.topic || 'Content Package'}</div>
                  <div className="text-xs text-gray-400">{weapon.desc} • {pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : 'Recent'}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-orange-400 transition-colors" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Olympus Alliance Panel (Athena + Hermes)
const OlympusAlliance = ({ onTriggerAlliance }) => {
  const [allianceActive, setAllianceActive] = useState(false);

  const handleAllianceClick = () => {
    setAllianceActive(true);
    onTriggerAlliance?.();
    setTimeout(() => setAllianceActive(false), 3000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-900/90 to-purple-900/30 backdrop-blur-sm rounded-xl p-5 border border-purple-500/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-orange-500 flex items-center justify-center">
          <span className="text-xl">⚡</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">The Pantheon</h3>
          <p className="text-xs text-gray-400">Divine collaboration</p>
        </div>
      </div>
      
      {/* Alliance visualization */}
      <div className="relative py-6">
        <div className="flex items-center justify-between">
          {/* Athena */}
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-2 ${allianceActive ? 'animate-pulse ring-4 ring-amber-500/50' : ''}`}>
              <span className="text-3xl">🦉</span>
            </div>
            <div className="text-sm font-bold text-amber-400">Athena</div>
            <div className="text-xs text-gray-500">Spy</div>
          </div>
          
          {/* Connection line with energy flow */}
          <div className="flex-1 mx-4 relative">
            <div className="h-1 bg-gradient-to-r from-amber-500 via-purple-500 to-orange-500 rounded-full" />
            {allianceActive && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-full animate-ping" />
                <Zap className="absolute w-6 h-6 text-yellow-400 animate-pulse" />
              </div>
            )}
            <div className="absolute top-3 left-1/2 transform -translate-x-1/2">
              <span className="text-xs text-purple-400">Intel → Content</span>
            </div>
          </div>
          
          {/* Hermes */}
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mx-auto mb-2 ${allianceActive ? 'animate-pulse ring-4 ring-orange-500/50' : ''}`}>
              <span className="text-3xl">👟</span>
            </div>
            <div className="text-sm font-bold text-orange-400">Hermes</div>
            <div className="text-xs text-gray-500">Forge</div>
          </div>
        </div>
      </div>
      
      <button
        onClick={handleAllianceClick}
        disabled={allianceActive}
        className={`
          w-full py-3 rounded-lg font-bold text-sm transition-all duration-300
          ${allianceActive 
            ? 'bg-gradient-to-r from-purple-600 to-orange-600 text-white animate-pulse' 
            : 'bg-gradient-to-r from-purple-500/20 to-orange-500/20 text-purple-300 hover:from-purple-500/40 hover:to-orange-500/40 border border-purple-500/30'
          }
        `}
      >
        {allianceActive ? (
          <span className="flex items-center justify-center gap-2">
            <Zap className="w-4 h-4 animate-spin" />
            Divine Synergy Active!
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Play className="w-4 h-4" />
            Go to Pantheon
          </span>
        )}
      </button>
      
      <p className="text-xs text-center text-gray-500 mt-3">
        Athena finds gaps → Hermes forges content
      </p>
    </div>
  );
};

// Content Calendar Widget
const ContentCalendar = ({ scheduledContent = [] }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // Deterministic schedule pattern based on day index
  const scheduledDays = [0, 2, 4]; // Mon, Wed, Fri
  
  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-5 border border-orange-500/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Forge Schedule</h3>
            <p className="text-xs text-gray-400">Upcoming content</p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          const isScheduled = scheduledDays.includes(index);
          return (
            <div key={day} className="text-center">
              <div className="text-xs text-gray-500 mb-2">{day}</div>
              <div className={`
                h-12 rounded-lg border transition-all duration-200
                ${isScheduled 
                  ? 'bg-gradient-to-br from-orange-500/30 to-amber-500/30 border-orange-500/50' 
                  : 'bg-gray-800/50 border-gray-700/50'
                }
                flex items-center justify-center
              `}>
                {isScheduled && <Flame className="w-4 h-4 text-orange-400" />}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-gradient-to-br from-orange-500/30 to-amber-500/30 border border-orange-500/50" />
          <span className="text-gray-400">Scheduled</span>
        </div>
        <button className="text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-1">
          View All <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// Brand Voice Console (Eternal Flame) with Configuration Modal
const BrandVoiceConsole = () => {
  const [showModal, setShowModal] = useState(false);
  const [brandVoice, setBrandVoice] = useState({
    tone: 'professional',
    style: 'conversational',
    energy: 'dynamic',
    personality_traits: ['helpful', 'knowledgeable', 'approachable'],
    avoid_words: [],
    preferred_phrases: [],
    use_questions: true,
    use_stories: true,
    use_statistics: true,
    use_humor: false,
    sample_content: '',
    company_name: '',
    brand_tagline: ''
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [newPhrase, setNewPhrase] = useState('');

  const voiceTraits = [
    { trait: 'Tone', value: brandVoice.tone?.charAt(0).toUpperCase() + brandVoice.tone?.slice(1) || 'Professional', color: 'orange' },
    { trait: 'Style', value: brandVoice.style?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Conversational', color: 'amber' },
    { trait: 'Energy', value: brandVoice.energy?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Dynamic', color: 'yellow' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // First, get the user's content_engine agent
      const agentsRes = await fetch(`${API_URL}/api/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const agentsData = await agentsRes.json();
      const hermesAgent = agentsData.agents?.find(a => a.agent_type === 'content_engine');
      
      if (hermesAgent) {
        const response = await fetch(`${API_URL}/api/agents/${hermesAgent.id}/brand-voice`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(brandVoice)
        });
        
        if (response.ok) {
          setIsConfigured(true);
          setShowModal(false);
          alert('🔥 Brand Voice saved! Hermes will now write in your unique voice.');
        }
      } else {
        alert('Please hire Hermes first to configure Brand Voice');
      }
    } catch (err) {
      console.error('Error saving brand voice:', err);
      alert('Failed to save brand voice');
    } finally {
      setSaving(false);
    }
  };

  const addAvoidWord = () => {
    if (newWord.trim() && !brandVoice.avoid_words.includes(newWord.trim())) {
      setBrandVoice({ ...brandVoice, avoid_words: [...brandVoice.avoid_words, newWord.trim()] });
      setNewWord('');
    }
  };

  const addPreferredPhrase = () => {
    if (newPhrase.trim() && !brandVoice.preferred_phrases.includes(newPhrase.trim())) {
      setBrandVoice({ ...brandVoice, preferred_phrases: [...brandVoice.preferred_phrases, newPhrase.trim()] });
      setNewPhrase('');
    }
  };

  return (
    <>
      <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-5 border border-orange-500/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center relative">
            <span className="text-xl">🔥</span>
            {isConfigured && <div className="absolute inset-0 rounded-full animate-ping bg-orange-500/30" />}
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Eternal Flame</h3>
            <p className="text-xs text-gray-400">{isConfigured ? 'Your voice burns constant' : 'Configure your unique voice'}</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {voiceTraits.map((item) => (
            <div key={item.trait} className="flex items-center justify-between">
              <span className="text-sm text-gray-400">{item.trait}</span>
              <span className={`text-sm font-medium text-${item.color}-400 bg-${item.color}-500/10 px-3 py-1 rounded-full`}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
        
        <button 
          onClick={() => setShowModal(true)}
          className="w-full mt-4 py-2 border border-orange-500/30 rounded-lg text-sm text-orange-400 hover:bg-orange-500/10 transition-colors flex items-center justify-center gap-2"
        >
          <Settings className="w-4 h-4" />
          Configure Voice
        </button>
      </div>

      {/* Brand Voice Configuration Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-orange-500/30">
            <div className="p-6 border-b border-gray-700 sticky top-0 bg-gray-900 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">🔥</span>
                  <div>
                    <h2 className="text-xl font-bold text-white">Eternal Flame Configuration</h2>
                    <p className="text-sm text-gray-400">Train Hermes to write in YOUR unique voice</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Core Voice Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tone</label>
                  <select 
                    value={brandVoice.tone}
                    onChange={(e) => setBrandVoice({...brandVoice, tone: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="enthusiastic">Enthusiastic</option>
                    <option value="educational">Educational</option>
                    <option value="persuasive">Persuasive</option>
                    <option value="friendly">Friendly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Style</label>
                  <select 
                    value={brandVoice.style}
                    onChange={(e) => setBrandVoice({...brandVoice, style: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="storytelling">Storytelling</option>
                    <option value="data_driven">Data-Driven</option>
                    <option value="conversational">Conversational</option>
                    <option value="authoritative">Authoritative</option>
                    <option value="inspirational">Inspirational</option>
                    <option value="educational">Educational</option>
                    <option value="humorous">Humorous</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Energy</label>
                  <select 
                    value={brandVoice.energy}
                    onChange={(e) => setBrandVoice({...brandVoice, energy: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="calm">Calm</option>
                    <option value="balanced">Balanced</option>
                    <option value="dynamic">Dynamic</option>
                    <option value="high_energy">High Energy</option>
                  </select>
                </div>
              </div>

              {/* Content Elements */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Content Elements</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'use_questions', label: 'Use Questions', desc: 'Rhetorical questions to engage readers' },
                    { key: 'use_stories', label: 'Use Stories', desc: 'Anecdotes and real examples' },
                    { key: 'use_statistics', label: 'Use Statistics', desc: 'Data points and numbers' },
                    { key: 'use_humor', label: 'Use Humor', desc: 'Light wit and jokes' },
                  ].map((item) => (
                    <label key={item.key} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-800">
                      <input 
                        type="checkbox" 
                        checked={brandVoice[item.key]}
                        onChange={(e) => setBrandVoice({...brandVoice, [item.key]: e.target.checked})}
                        className="mt-1 rounded border-gray-600"
                      />
                      <div>
                        <div className="text-sm font-medium text-white">{item.label}</div>
                        <div className="text-xs text-gray-400">{item.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Words to Avoid */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Words/Phrases to Avoid</label>
                <div className="flex gap-2 mb-2">
                  <input 
                    type="text"
                    value={newWord}
                    onChange={(e) => setNewWord(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAvoidWord()}
                    placeholder="e.g., leverage, synergy"
                    className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <button onClick={addAvoidWord} className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/30">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brandVoice.avoid_words.map((word, i) => (
                    <span key={i} className="px-2 py-1 bg-red-900/30 text-red-400 text-xs rounded-full flex items-center gap-1">
                      {word}
                      <button onClick={() => setBrandVoice({...brandVoice, avoid_words: brandVoice.avoid_words.filter((_, idx) => idx !== i)})} className="hover:text-red-300">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Preferred Phrases */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Signature Phrases</label>
                <div className="flex gap-2 mb-2">
                  <input 
                    type="text"
                    value={newPhrase}
                    onChange={(e) => setNewPhrase(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPreferredPhrase()}
                    placeholder="e.g., Let's dive in, Here's the thing"
                    className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <button onClick={addPreferredPhrase} className="px-4 py-2 bg-green-600/20 text-green-400 rounded-lg text-sm hover:bg-green-600/30">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brandVoice.preferred_phrases.map((phrase, i) => (
                    <span key={i} className="px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded-full flex items-center gap-1">
                      {phrase}
                      <button onClick={() => setBrandVoice({...brandVoice, preferred_phrases: brandVoice.preferred_phrases.filter((_, idx) => idx !== i)})} className="hover:text-green-300">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Brand Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
                  <input 
                    type="text"
                    value={brandVoice.company_name}
                    onChange={(e) => setBrandVoice({...brandVoice, company_name: e.target.value})}
                    placeholder="e.g., iSupplyAI"
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Brand Tagline</label>
                  <input 
                    type="text"
                    value={brandVoice.brand_tagline}
                    onChange={(e) => setBrandVoice({...brandVoice, brand_tagline: e.target.value})}
                    placeholder="e.g., AI That Actually Works"
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              {/* Sample Content */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sample Content (Optional)</label>
                <p className="text-xs text-gray-500 mb-2">Paste a sample of your best writing so Hermes can learn your voice</p>
                <textarea 
                  value={brandVoice.sample_content}
                  onChange={(e) => setBrandVoice({...brandVoice, sample_content: e.target.value})}
                  placeholder="Paste a paragraph or two from your best blog post, email, or social media content..."
                  rows={4}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-400 hover:text-white">
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-white hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : '🔥 Save Brand Voice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Main Hermes Content Forge Component
const HermesContentForge = ({ API, user, onRunAgent, onBack }) => {
  const [contentPackages, setContentPackages] = useState([]);
  const [isForging, setIsForging] = useState(false);
  const [forgeTemperature, setForgeTemperature] = useState(65);
  const [stats, setStats] = useState({ reach: 45200, engagement: 3840 });
  const [loading, setLoading] = useState(true);

  // Fetch content packages
  const fetchPackages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // API prop already includes /api, API_URL doesn't
      const apiUrl = API || `${API_URL}/api`;
      
      // Fetch all outputs and filter for content packages client-side
      // (some outputs have agent_type=None but contain blog_post data)
      const response = await fetch(`${apiUrl}/agents/outputs/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allOutputs = data.outputs || [];
        
        // Filter for content engine outputs (have blog_post or package in output_data)
        const contentOutputs = allOutputs.filter(o => {
          const outputData = o.output_data || {};
          return outputData.blog_post || outputData.package || o.agent_type === 'content_engine';
        });
        
        console.log('[HermesForge] Found content packages:', contentOutputs.length);
        setContentPackages(contentOutputs);
        
        // Update temperature based on recent activity
        setForgeTemperature(Math.min(95, 40 + contentOutputs.length * 5));
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleLaunchContent = async (contentType) => {
    // If we have the onRunAgent callback, use it to navigate to agent runner
    if (onRunAgent) {
      onRunAgent('content_engine');
    } else {
      // Fallback: show forging animation
      setIsForging(true);
      setTimeout(() => {
        setIsForging(false);
      }, 3000);
    }
  };

  const handleTriggerAlliance = () => {
    // Trigger Athena → Hermes pipeline
    if (onRunAgent) {
      onRunAgent('content_engine');
    }
    console.log('Alliance triggered: Athena → Hermes pipeline');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white relative overflow-hidden">
      {/* Ember particles background */}
      <EmberParticles />
      
      {/* Forge glow effect at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-orange-600/20 via-orange-500/5 to-transparent pointer-events-none" />
      
      {/* Header */}
      <div className="relative z-10 border-b border-orange-500/20 bg-gray-900/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 flex items-center justify-center shadow-lg shadow-orange-500/30 relative">
                <span className="text-3xl">👟</span>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                  <Flame className="w-3 h-3 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-black bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent">
                  Hermes&apos; Content Forge
                </h1>
                <p className="text-sm text-gray-400">Divine content forged in celestial fire</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {onBack && (
                <button 
                  onClick={onBack}
                  className="px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-orange-500/30 transition-colors text-gray-400 hover:text-white text-sm font-medium"
                >
                  ← Back
                </button>
              )}
              <button 
                onClick={fetchPackages}
                className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-orange-500/30 transition-colors"
              >
                <RefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <div className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-lg border border-orange-500/30">
                <span className="text-sm font-bold text-orange-400">🔥 Forge Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Grid */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column */}
          <div className="space-y-6">
            <ForgeTemperature temperature={forgeTemperature} />
            <QuickLaunchPad onLaunch={handleLaunchContent} isForging={isForging} />
            <BrandVoiceConsole />
          </div>
          
          {/* Center Column */}
          <div className="space-y-6">
            <CaduceusDashboard reach={stats.reach} engagement={stats.engagement} />
            <DivineArsenal packages={contentPackages} />
          </div>
          
          {/* Right Column */}
          <div className="space-y-6">
            <ContentCalendar />
          </div>
          
        </div>
        
        {/* Bottom Stats Bar */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Package, label: 'Packages Forged', value: contentPackages.length, color: 'orange' },
            { icon: TrendingUp, label: 'Avg. Performance', value: '87%', color: 'green' },
            { icon: Clock, label: 'Time Saved', value: '42 hrs', color: 'blue' },
            { icon: Award, label: 'Quality Score', value: '9.2/10', color: 'purple' },
          ].map((stat) => (
            <div 
              key={stat.label}
              className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-orange-500/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <stat.icon className={`w-8 h-8 text-${stat.color}-400`} />
                <div>
                  <div className="text-xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-gray-500">{stat.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(249, 115, 22, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(249, 115, 22, 0.7);
        }
      `}</style>
    </div>
  );
};

export default HermesContentForge;
